import { AfterViewInit, Component, Inject, OnDestroy, Optional, ViewEncapsulation } from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogRef
} from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AppNotificationService } from '@core/services/app-notification.service';
import { ConfirmDialogService } from '@shared/components/dialogs/confirm-dialog/confirm-dialog.service';
import { BcmWorkflowApiService } from '@bcmApiServices/bcm-workflow-api.service';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { Contract, ContractFileType } from '@shared/models/contract';
import { Person } from '@shared/models/person';
import { Company } from '@shared/models/company';
import { Boat, distinctBerths } from '@shared/models/boat';
import { BerthBoatAssignment } from '@shared/models/berth-boat-assignment';
import { resolveContractBerthAssignments } from '@bcm-work-flows/contract-creator/contract-creator-berths.util';
import { isString } from '@shared/functions/is-string';
import { U2bValidators } from '@shared/validators/validators';
import { BcmContractsStoreService } from '@bcmServices/contracts/bcm-contracts-store.service';
import { contractShortCodeEntries } from '@modules/bcm/@shared/enums/contract-short-codes';
import { findKnownShortcodes, findUnknownShortcodes } from '@shared/functions/find-short-codes';
import { missingContractFieldsPerson } from '@bcm-work-flows/contract-creator/missing-contract-fields-person';
import { BcmService } from '@modules/bcm/bcm.service';
import { missingContractFieldsCompany } from '@bcm-work-flows/contract-creator/missing-contract-fields-company';
import { Key } from '@shared/models/key';
import { Berth } from '@shared/models/berth';
import { faFileWord } from '@fortawesome/free-solid-svg-icons';
import { BcmBooking } from '@shared/models/bcm-booking';
import { TranslationService } from '@core/translation/translation.service';
import { BcmDynamicPriceContext } from '@shared/models/bcm-dynamic-price';
import { BcmSettingsFacade } from '@bcmServices/settings/bcm-settings-facade';
import { BcmSettingsSectionName, DefaultBerthReservationTimeUnit } from '@shared/models/bcm-settings';

@Component({
    selector: 'contract-creator',
    templateUrl: './contract-creator.component.html',
    styleUrls: ['./contract-creator.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ContractCreatorComponent implements AfterViewInit, OnDestroy {

    faFileWord = faFileWord;

    private _unsubscribeAll = new Subject<any>();

    public ContractFileType: typeof ContractFileType = ContractFileType;

    formGroup: UntypedFormGroup;

    availableContracts: (Contract & {group: string})[];

    loadingContracts = false;

    availableBoats: Boat[];

    availableBerths: Berth[] = [];

    /** Bookings the berth options are derived from - the booking's own when the dialog was opened
     *  from a booking, else the boat's. The boat nested in a booking carries none. (#6958) */
    berthAssignments: BerthBoatAssignment[] = [];

    selectedContract: Contract;

    person: Person;

    company: Company;

    foundShortCodes = {};

    knownShortCodeList: string[] = [];

    unknownShortCodeList: string[] = [];

    unknownShortCodeForm: UntypedFormGroup;

    hasCoOwner: boolean;

    hasBkz: boolean;

    hasProducts: boolean;

    hasBoat: boolean;

    hasBerth: boolean;

    hasKey: boolean;

    contractTemplateMissing: boolean;

    isSaving: boolean;

    bookingFromDate: Date;
    bookingToDate: Date;

    /** Rebuilt (new reference) whenever boat, berth or the date range changes, so the dynamic price
     *  panel re-evaluates - it reacts to `ngOnChanges`, not to in-place mutation. (#6959) */
    dynamicPriceContext: BcmDynamicPriceContext;

    defaultBerthReservationTimeUnit: DefaultBerthReservationTimeUnit;

    productDynamicPriceHasError: boolean;

    readonly contractAutocompleteGroupOrder = [
        TranslationService.translate('workflows.screens.contractCreator.contractGroupMatching'),
        TranslationService.translate('workflows.screens.contractCreator.contractGroupOther'),
    ];

    missingFields: {
        tenant: string[],
        person?: string[],
        company?: string[],
        boat: string[],
        berth: string[],
    } = {
        tenant: [],
        person: [],
        company: [],
        boat: [],
        berth: [],
    };

    constructor(private _dialogRef: MatDialogRef<ContractCreatorComponent>,
                @Optional() @Inject(MAT_DIALOG_DATA) public data: {
                    booking?: BcmBooking,
                    person?: Person,
                    company?: Company,
                    boat?: Boat,
                    fromDate?: Date,
                    toDate?: Date,
                },
                private _appNotificationService: AppNotificationService,
                private _confirmDialogService: ConfirmDialogService,
                private _bcmWorkflowApiService: BcmWorkflowApiService,
                private _bcmContractsStoreService: BcmContractsStoreService,
                private _bcmService: BcmService,
                private _bcmSettingsFacade: BcmSettingsFacade,
                private _formBuilder: UntypedFormBuilder) {
        this._bcmContractsStoreService.fetchAll();
        this._dialogRef.addPanelClass('default-dialog');
        this._dialogRef.disableClose = true;

        this.formGroup = this._formBuilder.group({
            contract: [null, [U2bValidators.required(TranslationService.translate('common.validationMsg.selectContractTemplate'))]],
            person: [],
            company: [],
            boat: [],
            berths: [[]],
            contractCoOwner: [],
            sendMail: [false],
            downloadPdf: [true],
            contractFileType: [ContractFileType.MAIL],
            key: [],
        });

        let {person, company, boat} = data;

        if (data.booking) {
            person = data.booking.person;
            company = data.booking.company;
            boat = data.booking.boat;
        }

        if (data.fromDate) {
            this.bookingFromDate = data.fromDate;
        }

        if (data.toDate) {
            this.bookingToDate = data.toDate;
        }

        let tenantRelationIds: number[] = [];
        let boats: Boat[];

        if (person) {

            this.formGroup.patchValue({person, company: null}, {emitEvent: false});

            ({boats} = person);
            tenantRelationIds = person.activeAndNextTenantRelationAssignments.map(tr => tr.tenantRelation.id);
        }

        if (company) {
            this.formGroup.patchValue({company, person: null}, {emitEvent: false});

            ({boats} = company);
            tenantRelationIds = company.activeAndNextTenantRelationAssignments.map(tr => tr.tenantRelation.id);
        }

        if (boat) {
            // needs to be done after person and company
            boats = [boat];
        }

        if (boats?.length) {
            this.availableBoats = boats;

            if (boats?.length === 1) {
                const singleBoat: Boat = boats[0];

                this.formGroup.patchValue({
                    boat: singleBoat,
                });

                this._setAvailableBerthsForBoat(singleBoat);
            }
        }

        this.person = person;
        this.company = company;

        this.defaultBerthReservationTimeUnit =
            this._bcmSettingsFacade.settings()[BcmSettingsSectionName.DefaultBerthReservationTimeUnit];

        this._buildDynamicPriceContext();

        this.formGroup.get('boat').valueChanges.subscribe(selectedBoat => {
            this._setAvailableBerthsForBoat(selectedBoat);

            this._buildDynamicPriceContext();
            this._refreshMissingFields();
        });

        this.formGroup.get('berths').valueChanges.subscribe(() => {
            this._buildDynamicPriceContext();
            this._refreshMissingFields();
        });

        const groupOther = TranslationService.translate('workflows.screens.contractCreator.contractGroupOther');
        const groupMatching = TranslationService.translate('workflows.screens.contractCreator.contractGroupMatching');

        this._bcmContractsStoreService.contracts$.subscribe(contracts => {
            this.availableContracts = contracts
                .map(contract => {

                    let group = groupOther;

                    if ((contract.tenantRelations || []).find(tr => tenantRelationIds.includes(tr.id))) {
                        group = groupMatching;
                    }

                    return {...contract, group};
                });
        });

        this.formGroup.get('contract').valueChanges.subscribe(contract => {
            this.selectedContract = contract;
            this.contractTemplateMissing = contract?.isWordTemplate && !contract?.templateMailFile && !contract?.templatePrintFile;

            this.foundShortCodes = {};

            contractShortCodeEntries.forEach(entry => this.foundShortCodes[entry[1]] = !!contract.content.includes(entry[1]));

            this.unknownShortCodeList = findUnknownShortcodes(contract?.content);
            this.unknownShortCodeForm = new UntypedFormGroup({});

            for (const item of this.unknownShortCodeList) {
                this.unknownShortCodeForm.addControl(item, new UntypedFormControl());
            }

            // todo: find all shortcodes which are not possible to fill because of missing data
            this.knownShortCodeList = findKnownShortcodes(contract?.content);
            this.hasCoOwner = !!this.knownShortCodeList.find(shortCode => !!shortCode.includes('VMI_'));
            this.hasBkz = !!this.knownShortCodeList.find(shortCode => !!shortCode.includes('BKZ_'));
            this.hasProducts = !!this.knownShortCodeList.find(shortCode => !!shortCode.includes('PRODUKT'));
            this.hasBoat = !!this.knownShortCodeList.find(shortCode => !!shortCode.includes('BOOT_'));
            this.hasBerth = !!this.knownShortCodeList.find(shortCode => !!shortCode.includes('LP_'));
            this.hasKey = !!this.knownShortCodeList.find(shortCode => !!shortCode.includes('SCHLUESSEL_NUMMER'));

            this._refreshMissingFields();
        });
    }

    ngAfterViewInit(): void {
        // The date range widget registers its group in a setTimeout, so it cannot be subscribed to
        // from the constructor.
        setTimeout(() => {
            this.formGroup.get('dateRangeForm')?.valueChanges
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => this._buildDynamicPriceContext());

            this._buildDynamicPriceContext();
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
    }

    get selectedBerths(): Berth[] {
        return this.formGroup.get('berths')?.value || [];
    }

    /** The single-berth placeholders ([LP_NAME], missing-data hints) still need one lead berth. */
    get selectedBerth(): Berth {
        return this.selectedBerths[0];
    }

    get isBerthRequiredButMissing(): boolean {
        return this.hasBerth
            && !!this.formGroup.get('boat')?.value
            && !this.selectedBerths.length;
    }

    get isContractActionDisabled(): boolean {
        return this.isSaving
            || !this.formGroup?.dirty
            || !this.availableContracts?.length
            || this.isBerthRequiredButMissing
            // A dynamic price with a criterion still missing has no evaluated price to hand over.
            || this.productDynamicPriceHasError;
    }

    /**
     * Everything the dynamic price rules can be evaluated against: who the contract is for, which
     * boat/berth it covers and over which period. A fresh object each time - the panel diffs the
     * input reference. (#6959)
     */
    private _buildDynamicPriceContext(): void {
        const dateRangeForm = this.formGroup.get('dateRangeForm');

        this.dynamicPriceContext = {
            person: this.person,
            company: this.company,
            fromDate: dateRangeForm?.get('from')?.value || this.bookingFromDate,
            toDate: dateRangeForm?.get('to')?.value || this.bookingToDate,
            preselection: {
                boat: this.formGroup.get('boat')?.value,
                berth: this.selectedBerth,
            },
        };
    }

    private _refreshMissingFields(): void {
        const boat = this.formGroup.get('boat').value || (this.availableBoats || [])[0];
        const berth = this.selectedBerth || (this.availableBerths || [])[0];

        // `this.person`, not `data.person` - opened from a booking the person comes from the
        // booking, and the company branch would then run with an empty company and report every
        // field as missing.
        this.missingFields = this.person
            ? missingContractFieldsPerson(
                this.knownShortCodeList,
                this._bcmService.tenant,
                this.person,
                boat,
                berth,
            )
            : missingContractFieldsCompany(
                this.knownShortCodeList,
                this._bcmService.tenant,
                this.company,
                boat,
                berth,
            );
    }

    private _setAvailableBerthsForBoat(boat: Boat): void {
        this.berthAssignments = resolveContractBerthAssignments(this.data?.booking, boat);
        this.availableBerths = distinctBerths(this.berthAssignments);

        this.formGroup.patchValue({berths: [...this.availableBerths]});
    }

    onClickClose(): void {

        if (this.formGroup.dirty) {
            return this._confirmDialogService
                .useWarnTheme()
                .setTitle(TranslationService.translate('common.confirm.attention'))
                .setBody(TranslationService.translate('common.unsavedChanges.leavePageBody'))
                .setYesButton({color: 'warn', text: TranslationService.translate('common.unsavedChanges.leavePageConfirmYes')})
                .setNoButton({color: 'accent', text: TranslationService.translate('common.cancel')})
                .openWithCallback(() => this._dialogRef.close());
        }

        this._dialogRef.close(false);
    }

    onClickSaveAndClose(preview = false): void {
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            this._appNotificationService.showError(TranslationService.translate('common.checkFieldsHighlight'));
        } else {

            this.isSaving = true;

            const {
                person,
                company,
                boat,
                berths,
                contract,
                sendMail,
                downloadPdf,
                contractFileType,
                dateRangeForm,
                boatForm,
                signatureForm,
                contractCoOwnerForm,
                contractBuildingCostSubsidyForm,
                productsForm,
            } = this.formGroup.getRawValue();

            const unknownShortCodes = this.unknownShortCodeForm.getRawValue();

            // Only what the contract generator needs - the raw rows also carry the dynamic price
            // panel's own form group with fully hydrated boat/berth/relation objects. (#6959)
            const products = (productsForm?.products || []).map(({product, quantity, price, dynamicPrice}) => ({
                product,
                quantity,
                price: price ?? product?.price,
                dynamicPrice: dynamicPrice || null,
            }));

            const body: any = {
                preview,
                bcm_bookings_id: this.data?.booking?.id,
                person,
                company,
                boat,
                berths,
                // `berth` stays in the payload for everything still reading a single berth. (#6958)
                berth: (berths || [])[0] || null,
                contract,
                sendMail,
                downloadPdf,
                contractFileType: ContractFileType[contractFileType],
                fromDate: dateRangeForm.from,
                toDate: dateRangeForm.to,
                contractCoOwnerForm,
                contractBuildingCostSubsidyForm,
                productsForm: {products},
                unknownShortCodes
            };

            if (!boat && boatForm) {
                let theBoat: any = {};

                if (boatForm.boat && boatForm.boat.id) {
                    theBoat.id = boatForm.boat.id;
                } else {
                    delete boatForm.boat;
                    theBoat = {...boatForm};
                }

                body['boat'] = theBoat;
            }

            if (signatureForm?.sign === true) {
                body['sign'] = signatureForm;
            }

            this._bcmWorkflowApiService
                .contractCreation(body, this._appNotificationService)
                .add(() => {
                    this.isSaving = false;

                    if (!preview) {
                        this._dialogRef.close(true);
                    }
                });
        }
    }

    compareBoats(b1: Boat, b2: Boat): boolean {
        return b1?.id === b2?.id;
    }

    compareBerth(b1: Berth, b2: Berth): boolean {
        return b1?.id === b2?.id;
    }

    compareKeys(k1: Key, k2: Key): boolean {
        return k1?.id === k2?.id;
    }
}
